import * as React from "react";
import { FormikProps, Formik } from "formik";
import * as yup from "yup";
import SemanticField from "../../SemanticField";
import { Divider, Label, Form, Message, Radio, Container, FormField, FormInput, FormDropdown, FormButton } from "@/components/bp/bp-ui";
import { IDropdownItem } from "../IDropdownItem";
import { FormErrorList } from "../FormErrorList";
import { TimeZonePicker } from "../TimeZonePicker";
import { ClientResources, LabClient, UserClient, LabCreateModel, TranslationClient } from "@app/api2/api";
import { TranslationContext } from "../Contexts/TranslationContext";
import { PageHeader } from "@common/PageHeader";
import { BPContainer } from "@/components/bp/bpcontainer";

class ExtendedLabCreateModel implements LabCreateModel {
    userType?: "newuser" | "existinguser" = "newuser";
    timeZone?: string = "America/Chicago";
    country: string = "us";
    copyFromLab?: number = null;
    existingUserEmail?: string = '';
    name: string = '';
    newUserEmail?: string = '';
    newUserName?: string = '';
    locale?: string = '';
}

class LabCreateState {
    labList: IDropdownItem<number>[];
    loading: boolean = true;
    userType: string;
    userList: IDropdownItem<string>[];
    userSearch: string;
    userSearching: boolean;
    showServerErrors: boolean;
    serverErrors: string[];
    formData: ExtendedLabCreateModel = new ExtendedLabCreateModel();
    localeList: Array<IDropdownItem<string>>;
}

interface LabCreateProps {
}

const errorLabel = <Label color="red" pointing />;

export class LabCreate extends React.Component<LabCreateProps, LabCreateState> {
    
    private labClient = new LabClient();
    private userClient = new UserClient();
    private translationClient = new TranslationClient();
    labCreateSchema: yup.SchemaOf<any>;

    constructor(props, context) {
        super(props, context);

        this.state = new LabCreateState();

        this.labCreateSchema = yup.object().shape({
            name: yup.string().required().label(window.translation.lab.name),
            timeZone: yup.string().required().label(window.translation.lab.timeZone),
            userType: yup.string(),
            locale: yup.string().required().label(window.translation.common.language),
            newUserName: yup.string().when("userType", (userType, schema) => {
                if (userType === "newuser") {
                    return schema.required()
                } else {
                    return schema;
                }
            }).label(window.translation.lab.newUserName),
            newUserEmail: yup.string().email().when("userType", (userType, schema) => {
                if (userType === "newuser") {
                    return schema.required()
                } else {
                    return schema;
                }
            }).label(window.translation.lab.newUserEmail),
            existingUserEmail: yup.string().when("userType", (userType, schema) => {
                if (userType === "existinguser") {
                    return schema.required()
                } else {
                    return schema;
                }
            }).label(window.translation.lab.existingUser)
        });
    }

    async componentDidMount(): Promise<any> {
        const labs = await this.labClient.getAll()

        const labList = labs.labs.map((l) => ({ key: l.id, value: l.id, text: l.name }))
        const locales = await this.translationClient.getLocales();
        this.setState({
            labList: labList,
            loading: false,
            localeList: locales.map((l) => ({ key: l.code, value: l.code, text: l.name })),
        });

    }


    submit = async (values: LabCreateModel): Promise<any> => {
        const result = await this.labClient.create(values);
        if (result.success) {
            window.location.assign(result.url);
        } else {
            this.setState(
                {
                    showServerErrors: true,
                    serverErrors: result.errors
                });
        }
    };

    handleUserSearchChange = async (changeEvent, newValue): Promise<any> => {
        this.setState({
            userSearching: true,
            userSearch: newValue.searchQuery
        });

        const results = await this.userClient.search(newValue.searchQuery);

        this.setState({
            userSearching: false,
            userList: (results.foundUsers ? results.foundUsers.map((u) => ({ key: u.email, value: u.email, text: `${u.name} [${u.email}]` })) : [])
        });

    };
    handleUserTypeChange = (changeEvent, newValue) => {
        this.setState({ userType: newValue });
    };

    renderForm = (props: FormikProps<ExtendedLabCreateModel>) => {

        var errorList = Object.getOwnPropertyNames(props.errors)
            .filter((k) => props.touched[k])
            .map((k) => props.errors[k]);

        const handleTimeZoneChange = async (event, newValue) => {
            await props.setFieldValue("timeZone", newValue.zone);
            await props.setFieldValue("country", newValue.country);
        };

        const handleRadioChange = async (e, newValue) => {
            // console.log(e, newValue);
            await props.setFieldValue("userType", newValue.value);
        };
        // console.log(props.errors, props.isValid);
        return (
            <TranslationContext.Consumer>{t =>
                <Form loading={this.state.loading} onSubmit={props.handleSubmit} >
                    <div className="ui segment">
                        <SemanticField component={FormInput} name="name" label={t.lab.name} value={props.values.name} error={props.errors.name && props.touched.name} />
                        <SemanticField component={FormDropdown} name="locale" label={t.common.language} value={props.values.locale} selection options={this.state.localeList} />
                        <FormField>
                            <Radio label={t.lab.createNewAdmin} name="userType" value="newuser" checked={props.values.userType === "newuser"} onChange={handleRadioChange} />
                        </FormField>
                        {props.values.userType === "newuser" ? (
                            <>
                                <SemanticField component={FormInput} name="newUserName" label={t.lab.newUserName} value={props.values.newUserName} error={props.errors.newUserName && props.touched.newUserName} />
                                <SemanticField component={FormInput} name="newUserEmail" label={t.lab.newUserEmail} value={props.values.newUserEmail} error={props.errors.newUserEmail && props.touched.newUserEmail} />
                            </>
                        ) : ""}
                        <FormField>
                            <Radio label={t.lab.useExistingAdmin} name="userType" value="existinguser" checked={props.values.userType === "existinguser"} onChange={handleRadioChange} />
                        </FormField>

                        {props.values.userType === "existinguser" ? (
                            <SemanticField component={FormDropdown} search selection name="existingUserEmail" label={t.lab.existingUser} loading={this.state.userSearching} value={props.values.existingUserEmail} onSearchChange={this.handleUserSearchChange} options={this.state.userList} errorLabel={errorLabel} />
                        ) : ""}

                        <FormField control={TimeZonePicker} name="timeZone" label={t.lab.timeZone} value={props.values.timeZone} country={props.values.country} error={props.errors.timeZone && props.touched.timeZone} onChange={handleTimeZoneChange} />
                        <SemanticField component={FormDropdown} name="copyFromLab" label={t.lab.copyDefaultsFrom} search selection options={this.state.labList} value={props.values.copyFromLab} />
                        <Divider />
                        <FormButton disabled={!props.isValid}>{t.common.save}</FormButton>
                        <Message error header={t.common.errorsOnForm} list={errorList} visible={errorList.length > 0} />
                        <FormErrorList message={t.lab.errorCreatingLab} show={this.state.showServerErrors} errors={this.state.serverErrors} />
                    </div>
                </Form>
            }</TranslationContext.Consumer>
        );
    };

    render() {
        return (
            <BPContainer>
                <PageHeader header={window.translation.labView.create}></PageHeader>
                {!this.state.loading &&
                    <Formik initialValues={this.state.formData} onSubmit={this.submit} validationSchema={this.labCreateSchema}>
                        {(props) => {
                            return this.renderForm(props)
                        }}
                    </Formik>
                }
            </BPContainer>);
    }
}